// Service-worker registration and the update flow for the installable Lately
// shell (public/sw.js). Browser-API only: the worker precaches the app shell
// so it loads offline; mail state is the Swift core's (OPFS). This module
// owns registration, "a new build is waiting" detection, and the reload-on-
// activate handshake. The push message types the worker forwards
// ('lately-push-verification' / 'lately-push-state-change') are consumed by
// the web-push side (src/core/push.ts).
//
// Registered in production builds; in dev only with `?sw=1` in the URL (the
// dev worker is a passthrough — no hashed bundles to precache — so HMR keeps
// working while the register/update flow can still be exercised).

export const SW_URL = "/sw.js";
export const SKIP_WAITING_MESSAGE = "lately-skip-waiting";

/**
 * What the update banner has to say: a newer build is installed and waiting
 * for this page's go-ahead, or another tab already activated one (this page
 * still runs the old bundle; anything it lazily loads from the old cache is
 * gone, so a reload is the cure).
 */
export type PwaUpdate =
  | { kind: "waiting"; worker: ServiceWorker }
  | { kind: "activated-elsewhere" };

export type PwaUpdateListener = (update: PwaUpdate | null) => void;

const listeners = new Set<PwaUpdateListener>();
let registration: ServiceWorkerRegistration | null = null;
let update: PwaUpdate | null = null;
let reloadArmed = false;
let registering: Promise<ServiceWorkerRegistration | null> | null = null;

function notify() {
  for (const listener of listeners) {
    listener(update);
  }
}

function setUpdate(next: PwaUpdate | null) {
  if (
    next === update ||
    (next?.kind === "waiting" && update?.kind === "waiting" && next.worker === update.worker) ||
    (next?.kind === "activated-elsewhere" && update?.kind === "activated-elsewhere")
  ) {
    return;
  }
  update = next;
  notify();
}

function setWaiting(worker: ServiceWorker | null) {
  setUpdate(worker ? { kind: "waiting", worker } : null);
}

/** True when this page load should register the worker (see module note). */
export function shouldRegisterServiceWorker(
  location: Pick<Location, "search"> = window.location,
  isProduction: boolean = import.meta.env.PROD,
): boolean {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return false;
  }
  if (isProduction) {
    return true;
  }
  return new URLSearchParams(location.search).get("sw") === "1";
}

function watchInstalling(reg: ServiceWorkerRegistration) {
  const installing = reg.installing;
  if (!installing) {
    return;
  }
  installing.addEventListener("statechange", () => {
    if (installing.state === "installed" && navigator.serviceWorker.controller) {
      // A build is installed behind the one controlling this page: offer it.
      setWaiting(reg.waiting ?? installing);
    }
  });
}

/**
 * Register /sw.js at scope '/'. Resolves to the registration (or null when
 * the browser has no service workers / registration is skipped). Idempotent.
 */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (registering) {
    return registering;
  }
  registering = (async () => {
    if (!shouldRegisterServiceWorker()) {
      return null;
    }
    // A waiting worker taking over (after we asked it to skip waiting) means
    // the shell on disk changed under us: reload once so the page runs the
    // build it will be served from now on. When another tab asked (this one
    // did not arm the reload) the session is left alone and the banner
    // offers the reload instead. The first controller (install + claim on a
    // fresh visit) is not an update.
    let hadController = navigator.serviceWorker.controller !== null;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      const wasControlled = hadController;
      hadController = navigator.serviceWorker.controller !== null;
      if (reloadArmed) {
        reloadArmed = false;
        window.location.reload();
        return;
      }
      if (wasControlled) {
        setUpdate({ kind: "activated-elsewhere" });
      }
    });
    try {
      const reg = await navigator.serviceWorker.register(SW_URL, { scope: "/" });
      registration = reg;
      if (reg.waiting && navigator.serviceWorker.controller) {
        setWaiting(reg.waiting);
      }
      watchInstalling(reg);
      reg.addEventListener("updatefound", () => watchInstalling(reg));
      // Check for a new build whenever the app comes back to the foreground
      // (installed apps stay open for days; the browser's own 24h check is
      // the floor, not the ceiling).
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          reg.update().catch(() => undefined);
        }
      });
      return reg;
    } catch (error) {
      console.warn("[lately] service worker registration failed:", error);
      return null;
    }
  })();
  return registering;
}

// Chromium fires `beforeinstallprompt` once the page is installable (manifest
// + icons + a fetch-handling worker in control). Kept for an in-app "Install"
// affordance later; logged so installability shows up in the console.
let deferredInstallPrompt: (Event & { prompt?: () => Promise<unknown> }) | null = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    deferredInstallPrompt = event;
    console.info("[lately] beforeinstallprompt: the app is installable");
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    console.info("[lately] appinstalled");
  });
}

/** The deferred Chromium install prompt, if the browser offered one. */
export function installPrompt(): (Event & { prompt?: () => Promise<unknown> }) | null {
  return deferredInstallPrompt;
}

/** The registration, once `registerServiceWorker()` resolved. */
export function serviceWorkerRegistration(): ServiceWorkerRegistration | null {
  return registration;
}

/**
 * Subscribe to update state changes -- a new build waiting, or one activated
 * by another tab (called immediately with the current state).
 */
export function onUpdateAvailable(listener: PwaUpdateListener): () => void {
  listeners.add(listener);
  listener(update);
  return () => {
    listeners.delete(listener);
  };
}

/** The current update state, if a newer build is waiting or already active elsewhere. */
export function updateAvailable(): PwaUpdate | null {
  return update;
}

/**
 * Apply the update: for a waiting build post `lately-skip-waiting`; the
 * worker calls `skipWaiting()`, `controllerchange` fires, and the page
 * reloads onto the new shell. When another tab already activated the build,
 * just reload onto it. No-op (false) when there is nothing to apply.
 */
export function applyUpdate(): boolean {
  if (update?.kind === "activated-elsewhere") {
    window.location.reload();
    return true;
  }
  const worker =
    (update?.kind === "waiting" ? update.worker : null) ?? registration?.waiting ?? null;
  if (!worker) {
    return false;
  }
  reloadArmed = true;
  worker.postMessage({ type: SKIP_WAITING_MESSAGE });
  return true;
}
